'use client'

import { useState, useEffect, useCallback, useRef } from 'react'

interface MailingListProps {
  className?: string
  onSuccess?: () => void
  uniqueId?: string
}

interface HCaptcha {
  render(_elementId: string, _config: { sitekey: string; callback(_response: string): void; 'expired-callback'(): void }): string;
  reset(_id: string): void;
  remove(_id: string): void;
}

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    hcaptcha: HCaptcha;
  }
}

export default function MailingList({ className = '', onSuccess, uniqueId = 'default' }: MailingListProps) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')
  const [showCaptcha, setShowCaptcha] = useState(false)
  const [scriptError, setScriptError] = useState(false)
  const captchaContainerId = `hcaptcha-container-${uniqueId}`
  const captchaRef = useRef<{ widget?: string, token?: string }>({})
  const scriptLoaded = useRef(false)
  const captchaCreated = useRef(false)

  // Get hCaptcha site key from environment variable
  const hCaptchaSiteKey = process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY

  // Load hCaptcha script
  const loadHCaptchaScript = useCallback(() => {
    if (scriptLoaded.current) return

    // Check if script is already loaded
    if (document.querySelector('script[src*="hcaptcha"]')) {
      console.log('hCaptcha script already exists')
      scriptLoaded.current = true
      return
    }

    console.log('Loading hCaptcha script...')
    const script = document.createElement('script')
    script.src = 'https://js.hcaptcha.com/1/api.js'
    script.async = true
    script.defer = true
    script.onload = () => {
      console.log('hCaptcha script loaded successfully')
      scriptLoaded.current = true
      setScriptError(false)
      
      // Create captcha if it should be shown
      if (showCaptcha) {
        console.log('Script loaded and captcha should be shown, creating...')
        // Use a timeout to avoid circular dependency
        setTimeout(() => {
          if (showCaptcha) {
            createCaptcha() // eslint-disable-line react-hooks/exhaustive-deps
          }
        }, 100)
      }
    }
    script.onerror = () => {
      console.error('Failed to load hCaptcha script')
      setScriptError(true)
    }
    document.head.appendChild(script)
  }, [showCaptcha]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up captcha widget
  const cleanupCaptcha = useCallback(() => {
    if (captchaRef.current.widget && window.hcaptcha) {
      try {
        window.hcaptcha.remove(captchaRef.current.widget)
        captchaRef.current = {}
        captchaCreated.current = false
      } catch (e) {
        console.error('Error removing hCaptcha:', e)
      }
    }
    
    // Additional cleanup for any remaining hCaptcha iframes
    try {
      const container = document.getElementById(captchaContainerId)
      if (container) {
        const iframes = container.querySelectorAll('iframe')
        iframes.forEach(iframe => {
          iframe.remove()
        })
        // Clear the container content
        container.innerHTML = ''
      }
    } catch (e) {
      console.error('Error cleaning up hCaptcha iframes:', e)
    }
  }, [captchaContainerId])

  // Handle form submission with captcha token
  const handleSubmitWithToken = useCallback(async (_token: string) => {
    setStatus('loading')
    setMessage('')
    
    try {
      console.log('Processing email subscription')
      
      // Call the API endpoint
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          message: `New mailing list subscription with captcha token: ${_token}`,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to subscribe')
      }

      console.log('Subscription successful:', data)
      
      setStatus('success')
      setMessage('Thanks for subscribing! We\'ll be in touch soon.')
      setEmail('')
      setShowCaptcha(false)
      cleanupCaptcha()
      onSuccess?.()
      
    } catch (error: unknown) {
      console.error('Error handling submission:', error)
      setStatus('error')
      setMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
    }
  }, [email, cleanupCaptcha, onSuccess])

  // Create captcha widget
  const createCaptcha = useCallback(() => {
    console.log('createCaptcha called - scriptLoaded:', scriptLoaded.current, 'showCaptcha:', showCaptcha, 'captchaCreated:', captchaCreated.current)
    
    if (!scriptLoaded.current) {
      console.log('Script not loaded yet, will retry')
      return
    }
    
    if (!showCaptcha) {
      console.log('Captcha not shown, skipping creation')
      return
    }
    
    if (captchaCreated.current) {
      console.log('Captcha already created, skipping')
      return
    }

    // Check for container
    const container = document.getElementById(captchaContainerId)
    if (!container) {
      console.error('Captcha container not found:', captchaContainerId)
      return
    }

    // Check if captcha already exists in this container
    if (container.querySelector('.h-captcha iframe')) {
      console.log('Captcha already exists in this container, skipping creation')
      captchaCreated.current = true
      return
    }

    try {
      console.log('Attempting to create hCaptcha widget')
      
      // Clean up any existing widget
      if (captchaRef.current.widget) {
        try {
          window.hcaptcha?.remove(captchaRef.current.widget)
          captchaRef.current = {}
        } catch (e) {
          console.error('Error removing existing hCaptcha:', e)
        }
      }

      // Clear container
      container.innerHTML = ''

      // Render new widget
      if (window.hcaptcha && hCaptchaSiteKey) {
        console.log('Rendering hCaptcha with site key:', hCaptchaSiteKey)
        const widgetId = window.hcaptcha.render(captchaContainerId, {
          sitekey: hCaptchaSiteKey,
          callback: (token) => {
            console.log('hCaptcha callback received with token:', token)
            captchaRef.current.token = token
            handleSubmitWithToken(token)
          },
          'expired-callback': () => {
            console.log('hCaptcha expired')
            captchaRef.current.token = undefined
          }
        })
        
        captchaRef.current.widget = widgetId
        captchaCreated.current = true
        console.log('hCaptcha widget created with ID:', widgetId)
      } else {
        console.error('hCaptcha not available on window or site key missing')
        setScriptError(true)
      }
    } catch (e) {
      console.error('Error creating hCaptcha widget:', e)
      setScriptError(true)
    }
  }, [showCaptcha, captchaContainerId, handleSubmitWithToken, hCaptchaSiteKey])

  // Load script when component mounts
  useEffect(() => {
    loadHCaptchaScript()
  }, [loadHCaptchaScript]) // eslint-disable-line react-hooks/exhaustive-deps

  // Create captcha when showCaptcha changes
  useEffect(() => {
    console.log('showCaptcha changed to:', showCaptcha)
    if (showCaptcha) {
      captchaCreated.current = false
      if (scriptLoaded.current) {
        console.log('Script already loaded, creating captcha...')
        setTimeout(createCaptcha, 100)
      } else {
        console.log('Script not loaded yet, will create when loaded')
      }
    } else {
      captchaCreated.current = false
      cleanupCaptcha()
    }
  }, [showCaptcha, createCaptcha, cleanupCaptcha])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupCaptcha()
    }
  }, [cleanupCaptcha])

  // Handle submission
  const handleSubmit = async (e?: React.FormEvent | React.KeyboardEvent) => {
    console.log('Submission started')
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    
    console.log('Submission prevented, checking email:', email)
    
    if (!email) {
      setStatus('error')
      setMessage('Please enter your email address')
      return
    }
    
    if (!showCaptcha) {
      console.log('Showing captcha')
      setShowCaptcha(true)
      return
    }

    console.log('Captcha token check:', captchaRef.current.token)

    if (!captchaRef.current.token) {
      setStatus('error')
      setMessage('Please complete the captcha verification')
      return
    }

    // If we have a token, submit the form
    if (captchaRef.current.token) {
      console.log('Processing submission with token')
      await handleSubmitWithToken(captchaRef.current.token)
    }
  }

  // Check for required configuration
  if (!hCaptchaSiteKey) {
    console.error('NEXT_PUBLIC_HCAPTCHA_SITE_KEY is not defined in environment variables')
    return <div className="text-red-500">Configuration error: hCaptcha site key not found</div>
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="flex-1 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSubmit(e as React.FormEvent)
            }
          }}
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={status === 'loading'}
          className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
        </button>
      </div>
      {showCaptcha && (
        <div id={captchaContainerId} className="h-captcha mt-4"></div>
      )}
      {scriptError && showCaptcha && (
        <p className="text-red-500 text-sm mt-2">
          Captcha failed to load. Please refresh the page and try again.
        </p>
      )}
      {message && (
        <p className={`mt-2 ${status === 'error' ? 'text-red-500' : 'text-green-500'}`}>
          {message}
        </p>
      )}
    </div>
  )
} 